/**
 * Which pages are which, headless (docs/features/order-to-invoice/87_aw_split_combined_file_plan.md §5–§8): the
 * state of the page tags a person checks on one file sent for every part of an item, and the request that confirms
 * them. Staff's screens, the basket and the customer's pages all tag the same way; each shell draws its own grid.
 *
 * What is here is bookkeeping on the person's own tags: which page carries which, which are left out, which are not
 * yet tagged, and the body the split posts. Every sentence on the step (the summary, the reasons, the size of a
 * page, the leave-out word) is the read's; the family decides what a valid assignment is and refuses the rest in
 * its field-tagged shape (`assignment.pages`), so this never rules a split in or out beyond "every page has a tag".
 */

import { useCallback, useMemo, useState } from "react";
import type { PendingCombinedFile } from "../data/api-types";

/** A page that goes to no part: tagged "Leave out" (staff) or "Not for printing" (a customer). */
export const LEFT_OUT = "__left_out__" as const;

/** A page's tag: a part's key, left out, or none yet. */
export type PageTag = string | typeof LEFT_OUT | null;

/** The tags by page number. */
export type PageTags = Readonly<Record<number, PageTag>>;

/** The body the split posts: each part's pages, in file order, and the pages left out. */
export interface SplitAssignment {
  assignment: Record<string, number[]>;
  left_out_pages: number[];
}

/** The tags the read proposes: a page the proposal leaves untagged is untagged. Left out is never proposed (doc 87 §5). */
export function proposedTags(pending: PendingCombinedFile): PageTags {
  return Object.fromEntries(
    pending.pages.map((page) => [page.page_no, page.proposed_part_key]),
  );
}

/** The pages with no tag yet, in order. A split needs every page tagged or left out (doc 87 §8.1). */
export function untaggedPages(
  pending: PendingCombinedFile,
  tags: PageTags,
): number[] {
  return pending.pages
    .map((page) => page.page_no)
    .filter((pageNo) => (tags[pageNo] ?? null) === null);
}

/** The split's body from the tags: pages in ascending order per part, a part with no page omitted. */
export function assignmentOf(
  pending: PendingCombinedFile,
  tags: PageTags,
): SplitAssignment {
  const assignment: Record<string, number[]> = {};
  const left_out_pages: number[] = [];
  for (const page of pending.pages) {
    const tag = tags[page.page_no] ?? null;
    if (tag === null) continue;
    if (tag === LEFT_OUT) left_out_pages.push(page.page_no);
    else (assignment[tag] ??= []).push(page.page_no);
  }
  return { assignment, left_out_pages };
}

/** How many files the split makes: the parts that have a page, not the pages left out. */
export function filesMade(request: SplitAssignment): number {
  return Object.values(request.assignment).filter((pages) => pages.length > 0)
    .length;
}

/** The button's label: "Split into 2 files". */
export function splitLabel(request: SplitAssignment): string {
  const n = filesMade(request);
  return `Split into ${n} ${n === 1 ? "file" : "files"}`;
}

/** Runs of consecutive pages, in the order given: `[1, 2, 35, 36]` is `["1–2", "35–36"]`. */
export function pageRuns(pages: readonly number[]): string[] {
  const runs: number[][] = [];
  for (const page of pages) {
    const last = runs[runs.length - 1];
    if (last && page === last[last.length - 1]! + 1) last.push(page);
    else runs.push([page]);
  }
  return runs.map((run) =>
    run.length === 1 ? String(run[0]) : `${run[0]}–${run[run.length - 1]}`,
  );
}

/** What each part is getting, as the person has tagged so far: `Cover 1–2, 11–12`. Parts with no page are left out of it. */
export function tagSummary(
  pending: PendingCombinedFile,
  tags: PageTags,
): Array<{ partKey: string; label: string; pages: string }> {
  const { assignment } = assignmentOf(pending, tags);
  return pending.parts.flatMap((part) => {
    const pages = assignment[part.part_key];
    return pages && pages.length > 0
      ? [
          {
            partKey: part.part_key,
            label: part.label,
            pages: pageRuns(pages).join(", "),
          },
        ]
      : [];
  });
}

/** The tag menu's value for a page tag (a select's value is a string; none is the empty string). */
export const tagToValue = (tag: PageTag): string => tag ?? "";
export const valueToTag = (value: string): PageTag =>
  value === "" ? null : value;

/**
 * The tags being checked, starting from the proposal. `reset` returns to it (a new pending file, or a
 * refused split the person wants to start over from).
 */
export function useCombinedFileTagging(pending: PendingCombinedFile) {
  const [tags, setTags] = useState<PageTags>(() => proposedTags(pending));
  const tag = useCallback((pageNo: number, next: PageTag) => {
    setTags((current) => ({ ...current, [pageNo]: next }));
  }, []);
  const reset = useCallback(() => setTags(proposedTags(pending)), [pending]);
  const untagged = useMemo(() => untaggedPages(pending, tags), [pending, tags]);
  const request = useMemo(() => assignmentOf(pending, tags), [pending, tags]);
  const summary = useMemo(() => tagSummary(pending, tags), [pending, tags]);
  return { tags, tag, reset, untagged, request, summary };
}
